import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  Droplets, 
  Layers, 
  Home, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle2,
  Star,
  Menu,
  X,
  ArrowRight,
  Award,
  Users,
  Languages
} from 'lucide-react';
import { companyInfo } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const LandingPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = getTranslation(language);
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const iconMap = {
    Droplets: Droplets,
    Layers: Layers,
    Home: Home
  };

  // Service images
  const serviceImages = {
    waterproofing: "https://images.unsplash.com/photo-1635424824849-1b09bdcc55b1",
    insulation: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f",
    roofing: "https://images.unsplash.com/photo-1635424709845-3a85ad5e1f5e"
  };

  // Form handling functions removed - using direct email contact

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_revamped-presence/artifacts/4b9631k0_isoflam_logo.jpg" 
                alt="ISOFLAM SARL"
                className="h-14 w-auto object-contain"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-6 items-center">
              <button onClick={() => scrollToSection('home')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.home}</button>
              <button onClick={() => scrollToSection('services')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.services}</button>
              <button onClick={() => scrollToSection('about')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.about}</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.testimonials}</button>
              <button onClick={() => scrollToSection('faq')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.faq}</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">{t.nav.contact}</button>
              
              {/* Language Switcher */}
              <button 
                onClick={toggleLanguage}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors font-medium"
                title="Change language"
              >
                <Languages className="h-4 w-4" />
                <span>{language === 'en' ? 'FR' : 'EN'}</span>
              </button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-slate-600 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-3">
              <button onClick={() => scrollToSection('home')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.home}</button>
              <button onClick={() => scrollToSection('services')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.services}</button>
              <button onClick={() => scrollToSection('about')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.about}</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.testimonials}</button>
              <button onClick={() => scrollToSection('faq')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.faq}</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium text-left">{t.nav.contact}</button>
              
              {/* Mobile Language Switcher */}
              <button 
                onClick={toggleLanguage}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors font-medium w-fit"
              >
                <Languages className="h-4 w-4" />
                <span>{language === 'en' ? 'Français' : 'English'}</span>
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587582423116-ec07293f0395?w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-blue-900/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {t.hero.title}
            <span className="block text-blue-300 mt-2">{t.hero.subtitle}</span>
          </h2>
          <p className="text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
            {t.hero.description} {companyInfo.experience}
          </p>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all hover:scale-105"
            onClick={() => scrollToSection('contact')}
          >
            {t.hero.cta} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">100%</div>
              <div className="text-blue-100 font-semibold text-lg">{t.statistics.quality.label}</div>
              <div className="text-blue-200 text-sm">{t.statistics.quality.subtitle}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">100%</div>
              <div className="text-blue-100 font-semibold text-lg">{t.statistics.trust.label}</div>
              <div className="text-blue-200 text-sm">{t.statistics.trust.subtitle}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">100%</div>
              <div className="text-blue-100 font-semibold text-lg">{t.statistics.security.label}</div>
              <div className="text-blue-200 text-sm">{t.statistics.security.subtitle}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">&gt; 1023</div>
              <div className="text-blue-100 font-semibold text-lg">{t.statistics.clients.label}</div>
              <div className="text-blue-200 text-sm">{t.statistics.clients.subtitle}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">{t.services.title}</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t.services.description}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Waterproofing */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={serviceImages.waterproofing} 
                  alt={t.services.waterproofing.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Droplets className="h-10 w-10 text-white" />
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-slate-800">{t.services.waterproofing.title}</CardTitle>
                <CardDescription className="text-slate-600">{t.services.waterproofing.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {t.services.waterproofing.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Insulation */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={serviceImages.insulation} 
                  alt={t.services.insulation.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Layers className="h-10 w-10 text-white" />
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-slate-800">{t.services.insulation.title}</CardTitle>
                <CardDescription className="text-slate-600">{t.services.insulation.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {t.services.insulation.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Roofing */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={serviceImages.roofing} 
                  alt={t.services.roofing.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Home className="h-10 w-10 text-white" />
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-slate-800">{t.services.roofing.title}</CardTitle>
                <CardDescription className="text-slate-600">{t.services.roofing.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {t.services.roofing.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">{t.about.title}</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {t.about.description}
              </p>
              <p className="text-xl font-semibold text-blue-600 mb-8">{t.about.values}</p>
              <div className="grid grid-cols-2 gap-4">
                {t.about.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <Award className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1672748341520-6a839e6c05bb" 
                alt="Professional team"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-lg shadow-xl">
                <div className="text-3xl font-bold">{companyInfo.experience}</div>
                <div className="text-blue-100">{t.about.experienceLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">{t.testimonials.title}</h2>
            <p className="text-xl text-slate-600">{t.testimonials.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {t.testimonials.items.map((testimonial, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-lg text-slate-800">{testimonial.name}</CardTitle>
                  <CardDescription className="text-slate-600">{testimonial.company}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 italic">&ldquo;{testimonial.content}&rdquo;</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">{t.faq.title}</h2>
            <p className="text-xl text-slate-600">{t.faq.subtitle}</p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {t.faq.items.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-slate-200 rounded-lg px-6 bg-slate-50">
                <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-blue-600 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Contact Information */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-800 mb-6">{t.contact.title}</h2>
              <p className="text-lg text-slate-600 mb-12">
                {t.contact.description}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2 text-lg">{t.contact.address}</h3>
                      <p className="text-slate-600">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2 text-lg">{t.contact.phone}</h3>
                      <p className="text-slate-600">{companyInfo.phone}</p>
                      <p className="text-slate-600">{companyInfo.phone2}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="flex flex-col items-center space-y-6">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-6 text-xl transition-all hover:scale-105 shadow-lg"
                onClick={() => window.location.href = `mailto:${companyInfo.email}`}
              >
                <Mail className="mr-3 h-6 w-6" />
                {t.contact.contactButton}
              </Button>
              
              <Card className="border-slate-200 shadow-lg bg-blue-600 text-white w-full max-w-md">
                <CardContent className="pt-6 text-center">
                  <Users className="h-12 w-12 mb-4 mx-auto" />
                  <h3 className="text-2xl font-bold mb-2">{t.contact.guarantee.title}</h3>
                  <p className="text-blue-100">{t.contact.guarantee.subtitle}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">{t.companyName}</h3>
              <p className="text-slate-300">
                {t.footer.description}
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">{t.footer.quickLinks}</h3>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-blue-400 transition-colors">{t.nav.services}</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-blue-400 transition-colors">{t.nav.about}</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="text-slate-300 hover:text-blue-400 transition-colors">{t.nav.testimonials}</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-blue-400 transition-colors">{t.nav.contact}</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">{t.footer.contactInfo}</h3>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start">
                  <MapPin className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{companyInfo.address}</span>
                </li>
                <li className="flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  <span>{companyInfo.phone}</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8 text-center text-slate-400">
            <p>&copy; {new Date().getFullYear()} {t.companyName}. {t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
